import moment from "moment";

export const formatExtraFields = (name) => {
  const map = {
    á: "a",
    ã: "a",
    é: "e",
    í: "i",
    ó: "o",
    ú: "u",
    ñ: "n",
    " ": "_",
    "¿": "",
    "?": "",
  };
  const joinedKeys = Object.keys(map)
    .map((key) => key)
    .join("");
  const regex = new RegExp(`[${joinedKeys}]`, "gi");
  return name.toLowerCase().replace(regex, (match) => map[match]);
};

export const formatPrice = (number) => {
  if (!number) {
    number = 0;
  }
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
  }).format(number);
};

export const getRangePeriod = (start, end) => ({
  startDate: moment()
    .subtract(...start)
    .toDate(),
  endDate: moment()
    .endOf(end || "day")
    .toDate(),
});

export const getDefaultRangePeriod = () => {
  let year = new Date().getFullYear();

  return {
    startDate: moment(`${year}-01-01`).toDate(),
    endDate: moment().toDate(),
  };
};

export const downloadFile = (data, type, name) => {
  const blob = new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.dataset.downloadUrl = [type, link.download, link.href].join(":");
  link.click();
  link.remove();
};

export const downloadLink = (url) => {
  // // const link = document.createElement('a')
  // // link.href = 'data:text/html;charset=utf-8,' + encodeURIComponent(url);
  // // link.download = 'Reference.html';
  // // link.style.display = 'none';
  // // // link.dataset.downloadUrl = ['application/pdf', link.download, link.href]
  // // link.click();
  // // link.remove();
};

export const getBearerToken = () => {
  const token = localStorage.getItem("USER_TOKEN");
  return token && `Bearer ${token}`;
};

/**
 * Groups registration fields into steps based on their assigned FormSection.
 * @param {Array} sections - Array of FormSection objects sorted by order.
 * @param {Array} fields - Array of RegistrationField objects.
 * @returns {Array} Array of step objects { _id, title, fields }.
 */
export const groupFieldsBySection = (sections, fields) => {
  if (!sections || sections.length === 0) {
    return [{ _id: null, title: null, fields: fields || [] }];
  }

  const sortedFields = [...(fields || [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );

  const unsectioned = sortedFields.filter(
    (f) =>
      !f.section ||
      !sections.find((s) => s._id === (f.section._id || f.section))
  );

  const sectionedGroups = sections.map((section) => ({
    _id: section._id,
    title: section.name,
    fields: sortedFields.filter(
      (f) => f.section && (f.section._id || f.section) === section._id
    ),
  }));

  const result = [];

  if (unsectioned.length > 0) {
    result.push({
      _id: null,
      title: "Campos sin sección",
      fields: unsectioned,
    });
  }

  result.push(...sectionedGroups);

  return result;
};

/**
 * Builds a lookup map of field definitions keyed by `formattedName`.
 * @param {Array} definitions - Array of RegistrationField definition objects.
 * @returns {Object} Map { formattedName: definition }.
 */
export const buildFieldDefMap = (definitions) => {
  const map = {};
  (definitions || []).forEach((def) => {
    if (def && def.formattedName) {
      map[def.formattedName] = def;
    }
  });
  return map;
};

/**
 * Enriches a member's stored `memberData` values with the live `order` and
 * `section` taken from the organization's current field definitions (matched
 * by `formattedName`). Falls back to the snapshot values stored on the member
 * entry when a matching definition is not found. DOC fields are excluded.
 *
 * @param {Array} memberData - The member's inline stored field values.
 * @param {Array} definitions - The org's live RegistrationField definitions.
 * @param {Object} [options] - Optional flags.
 * @param {boolean} [options.includeDocs=false] - Whether to keep DOC fields.
 * @returns {Array} Enriched field objects (new array, sorted stable by input order).
 */
export const enrichMemberDataWithDefs = (
  memberData,
  definitions,
  options = {}
) => {
  const { includeDocs = false } = options;
  if (!Array.isArray(memberData) || memberData.length === 0) return [];
  const defMap = buildFieldDefMap(definitions);
  return memberData
    .filter((field) => includeDocs || field.fieldType !== "DOC")
    .map((field) => {
      const def = defMap[field.formattedName] || {};
      return {
        ...field,
        order: def.order != null ? def.order : field.order,
        section:
          def.section != null
            ? def.section
            : field.section != null
            ? field.section
            : null,
      };
    });
};

/**
 * Sorts an array of fields ascending by `order` (nullish treated as 0).
 * Returns a new array (does not mutate the input).
 * @param {Array} fields - Array of field objects with an `order` property.
 * @returns {Array} Sorted copy.
 */
export const sortFieldsByOrder = (fields) => {
  if (!Array.isArray(fields)) return [];
  return [...fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

/**
 * Builds the effective `memberData` for a member by iterating over the
 * organization's live RegistrationField definitions (the source of truth)
 * and merging in any stored values from `memberData` matched by
 * `formattedName`. Fields the admin has added AFTER the member was created
 * are included as empty rows (value: null) so they show up as "pending to
 * fill" both in the preview and in the edit form.
 *
 * Stored entries that no longer have a matching definition (e.g. the admin
 * deleted the field) are kept by default so they are not silently dropped
 * on the next save. Pass `{ dropOrphaned: true }` to drop them instead.
 *
 * @param {Array} memberData - The member's inline stored field values.
 * @param {Array} definitions - The org's live RegistrationField definitions.
 * @param {Object} [options]
 * @param {Boolean} [options.includeDocs=false] - Whether to keep DOC fields.
 * @param {Boolean} [options.dropOrphaned=false] - Whether to drop stored entries with no matching definition.
 * @returns {Array} New array with one row per active definition, plus any orphans.
 */
export const buildMemberDataFromDefs = (
  memberData,
  definitions,
  options = {}
) => {
  const { includeDocs = false, dropOrphaned = false } = options;
  const defs = Array.isArray(definitions) ? definitions : [];
  const stored = Array.isArray(memberData) ? memberData : [];
  const storedByName = {};
  stored.forEach((entry) => {
    if (entry && entry.formattedName) storedByName[entry.formattedName] = entry;
  });

  const fromDefs = defs
    .filter((def) => includeDocs || def.fieldType !== "DOC")
    .map((def) => {
      const entry = storedByName[def.formattedName];
      return {
        ...def,
        value: entry ? entry.value : null,
        showInDataSheetByUser: entry ? entry.showInDataSheetByUser : undefined,
      };
    });

  if (dropOrphaned) return fromDefs;

  const defNames = new Set(defs.map((d) => d.formattedName));
  const orphans = stored.filter(
    (entry) =>
      entry &&
      entry.formattedName &&
      !defNames.has(entry.formattedName) &&
      (includeDocs || entry.fieldType !== "DOC")
  );

  return [...fromDefs, ...orphans];
};

export const isEmptySubmemberFieldValue = (value) => {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

export const buildSubmemberCustomFields = (
  fields,
  values = {},
  options = {}
) => {
  const { includeEmpty = false } = options;
  const customFields = {};
  (fields || []).forEach((field) => {
    if (!field?.formattedName) return;
    const value = values[field.formattedName];
    if (includeEmpty) {
      customFields[field.formattedName] =
        value === undefined ? null : value;
    } else if (!isEmptySubmemberFieldValue(value)) {
      customFields[field.formattedName] = value;
    }
  });
  return customFields;
};
